import {
  BASE_URL,
  GOOGLE_CALENDAR_DOMAIN,
  GOOGLE_MAIL_DOMAIN,
  OAUTH2_CLIENT_ID,
  OUTLOOK_DOMAINS,
} from "@config/constants";
import { generateCodeChallenge, generateCodeVerifier } from "@utils/cryptography.util";
import { getCodeVerifier, setAuthTokens, setCodeVerifier } from "@store/useAuthStore";
import { ItemType } from "@enums/common.enum";
import {
  findActiveGmailMessageId,
  setupGmailSelectionObserver,
} from "../extension/content-scripts/gmail";

interface LoginResult {
  authToken: string;
  refreshToken?: string;
}

export interface CalendarEventData {
  meetingId: string;
  calendarId: string;
}

enum GmailViewMode {
  DEFAULT = "default",
  SPLIT_VIEW = "split-view",
}

const GMAIL_THREAD_TOKEN_PATTERN = /^[A-Za-z0-9_-]{12,}$/;
const GMAIL_FOLDER_PREFIXES = ["category", "label", "search"];
const GMAIL_MAILBOXES = [
  "all",
  "drafts",
  "imp",
  "inbox",
  "scheduled",
  "sent",
  "snoozed",
  "spam",
  "starred",
  "trash",
];

/**
 * Initiates the OAuth2 PKCE login flow with Meridian using Chrome extension APIs.
 *
 * This function will:
 *   1. Generate a high-entropy code verifier and store it temporarily
 *   2. Build the correct authorize/login URLs for the OAuth2 web flow, using PKCE
 *   3. Launch a Chrome window for the user to log in and accept (chrome.identity.launchWebAuthFlow)
 *   4. On redirect, extract the authorization code from the redirect URL
 *   5. Exchange the authorization code (along with stored code verifier) for access and refresh tokens
 *   6. Save the acquired tokens to the auth store
 *   7. Return the tokens
 *
 * @returns {Promise<LoginResult>} Resolves to an object containing `authToken` and optionally `refreshToken` upon successful login.
 * @throws Will reject if the flow fails (e.g. user cancels, missing code, fetch error, etc.).
 *
 * Expected usage (inside async function):
 *   const result = await loginWithMeridian();
 *   // result.authToken and result.refreshToken
 */
export async function loginWithMeridian(): Promise<LoginResult> {
  const codeVerifier = generateCodeVerifier();
  const redirectUrl = chrome.identity.getRedirectURL();

  // Store code verifier in auth store for this flow
  setCodeVerifier(codeVerifier);

  const authParams = new URLSearchParams({
    response_type: "code",
    client_id: OAUTH2_CLIENT_ID,
    code_challenge: await generateCodeChallenge(codeVerifier),
    code_challenge_method: "S256",
    redirect_uri: redirectUrl,
  });

  const authorizeUrl = `${BASE_URL}o/authorize/?${authParams.toString()}`;
  const loginUrl = `${BASE_URL}auth/login/?login_callback=${encodeURIComponent(authorizeUrl)}`;

  // Launching interactive webflow via Chrome identity API
  return new Promise<LoginResult>((resolve, reject) => {
    chrome.identity.launchWebAuthFlow(
      {
        url: loginUrl,
        interactive: true,
      },
      async (responseUrl) => {
        if (chrome.runtime.lastError) {
          const error = chrome.runtime.lastError;
          console.error("FULL ERROR:", {
            message: error.message,
            popupBlocked: error.message.includes("could not be loaded"),
          });
          reject(new Error(chrome.runtime.lastError.message));
          return;
        }

        if (!responseUrl) {
          reject(new Error("No response URL received"));
          return;
        }

        try {
          const url = new URL(responseUrl);
          const code = url.searchParams.get("code");

          if (!code) {
            reject(new Error("No authorization code in response"));
            return;
          }

          const storedVerifier = getCodeVerifier();

          if (!storedVerifier) {
            reject(new Error("Code verifier not found in store"));
            return;
          }

          // Exchange authorization code (and code verifier) for tokens
          const tokenResponse = await fetch(`${BASE_URL}o/token/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams({
              grant_type: "authorization_code",
              code: code,
              redirect_uri: redirectUrl,
              client_id: OAUTH2_CLIENT_ID,
              code_verifier: storedVerifier,
            }).toString(),
          });

          if (!tokenResponse.ok) {
            const errorText = await tokenResponse.text();
            reject(new Error(`Token exchange failed: ${tokenResponse.status} - ${errorText}`));
            return;
          }

          const tokenData = await tokenResponse.json();

          // Store both tokens in auth store (both are always returned in OAuth flow)
          const authToken = tokenData.access_token;
          const refreshToken = tokenData.refresh_token;

          setAuthTokens(authToken, refreshToken);

          // Clean up code verifier from store
          setCodeVerifier(null);

          resolve({
            authToken,
            refreshToken,
          });
        } catch (error) {
          reject(new Error("Error during token exchange: " + error));
        }
      }
    );
  });
}

/**
 * Returns the numeric tab ID from the current window's `tid` URL param.
 *
 * @throws Error if `tid` is missing or invalid.
 */
export function getCurrentTabId(): number | null {
  const params = new URLSearchParams(window.location.search);
  const tid = +params.get("tid");

  if (!Number.isInteger(tid) || tid <= 0) {
    return null;
  }

  return tid;
}

/**
 * Returns URL of the tab associated with the current sidebar instance.
 *
 * @returns string of current tab URL, null if the tab is empty
 */
export async function getCurrentTabUrl(): Promise<string | null> {
  const tid = getCurrentTabId();
  if (!tid) return null;

  try {
    const tab = await chrome.tabs.get(tid);
    return tab?.url ?? null;
  } catch {
    return null;
  }
}

/**
 * Validates if a URL belongs to one of the allowed Outlook URLs.
 *
 * @param url - The URL to validate
 * @returns true if the URL contains any of the allowed Outlook URLs, false otherwise
 */
export function isValidOutlookUrl(url: string): boolean {
  try {
    return OUTLOOK_DOMAINS.includes(new URL(url).hostname);
  } catch {
    return false;
  }
}

/**
 * Validates if a URL belongs to one of the allowed Gmail URLs.
 *
 * @param url - The URL to validate
 * @returns true if the URL contains any of the allowed Gmail URLs, false otherwise
 */
export function isValidGoogleMailUrl(url: string): boolean {
  try {
    return new URL(url).hostname === GOOGLE_MAIL_DOMAIN;
  } catch {
    return false;
  }
}

/**
 * Validates if a URL belongs to one of the allowed Google calendar URLs.
 *
 * @param url - The URL to validate
 * @returns true if the URL contains any of the allowed Google calendar URLs, false otherwise
 */
export function isValidGoogleCalendarUrl(url: string): boolean {
  try {
    return new URL(url).hostname === GOOGLE_CALENDAR_DOMAIN;
  } catch {
    return false;
  }
}

/**
 * Parses a Gmail URL and determines its context.
 *
 * @param tabUrl - The URL to analyze
 * @returns
 * - GmailUrlContext.DIRECT_THREAD for thread URLs
 * - GmailUrlContext.SPLIT_VIEW for list views
 * - null otherwise
 */
function getGmailViewMode(tabUrl: string | null): GmailViewMode | null {
  if (!tabUrl) return null;

  try {
    const url = new URL(tabUrl);
    if (url.hostname !== GOOGLE_MAIL_DOMAIN) return null;

    const hash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;

    if (!hash) return null;

    const parts = hash.split("/");
    const first = parts[0];
    const last = parts[parts.length - 1];

    // Direct thread URL (`FMfcgz…`)
    if (GMAIL_THREAD_TOKEN_PATTERN.test(last)) {
      return GmailViewMode.DEFAULT;
    }

    // Split-view / list contexts: `#inbox`, `#starred`, `#label/Foo`, `#search/bar`
    if (GMAIL_FOLDER_PREFIXES.includes(first) || GMAIL_MAILBOXES.includes(last)) {
      return GmailViewMode.SPLIT_VIEW;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Determines the item type from a URL string.
 *
 * Gmail thread URLs look like:
 * - https://mail.google.com/mail/u/0/#inbox/FMfcgzQcqkwtLpXCTlNCmTTMBFRmBvCP
 * - https://mail.google.com/mail/u/0/#label/Important/FMfcgzQcqkwtLpXCTlNCmTTMBFRmBvCP
 *
 * Google Calendar event edit URLs look like:
 * - https://calendar.google.com/calendar/u/0/r/eventedit/NmQzYWJlYTNiYWE4NDBhNjk1MTIyYWE0MzExYmY4NTAgbW9ub2t1LmNvbV91MzRtam9rbzhxNjVlcTQyYzFrNzFlbThjb0Bn
 *
 * @param tabUrl - The URL to analyze
 * @returns ItemType.EMAIL if inside a Gmail thread, ItemType.MEETING if inside a Google Calendar event, ItemType.COMPANY if external URL, or null if on Google/Outlook but not viewing an item
 */
export function getItemTypeFromUrl(
  tabUrl: string | null
): ItemType.EMAIL | ItemType.MEETING | ItemType.COMPANY | null {
  if (!tabUrl) return null;

  try {
    const url = new URL(tabUrl);

    if (url.hostname === GOOGLE_MAIL_DOMAIN) {
      // Only return EMAIL for direct thread URLs, not for split view (list) URLs
      return getGmailViewMode(tabUrl) === GmailViewMode.DEFAULT ? ItemType.EMAIL : null;
    }

    if (url.hostname === GOOGLE_CALENDAR_DOMAIN) {
      return url.pathname.includes("/eventedit/") ? ItemType.MEETING : null;
    }
  } catch {
    return null;
  }

  if (isValidOutlookUrl(tabUrl)) return null;
  if (isValidGoogleMailUrl(tabUrl)) return null;
  if (isValidGoogleCalendarUrl(tabUrl)) return null;

  return ItemType.COMPANY;
}

/**
 * Gets the current Google item type based on the active tab URL and DOM state.
 * For Gmail split view, checks if a message is selected in the DOM.
 * @returns Promise resolving to the item type, or null if not applicable
 */
export async function getCurrentTabItemType(): Promise<
  ItemType.EMAIL | ItemType.MEETING | ItemType.COMPANY | null
> {
  const tabUrl = await getCurrentTabUrl();
  if (!tabUrl) return null;

  const itemType = getItemTypeFromUrl(tabUrl);
  if (itemType) return itemType;

  // For Gmail split view: URL doesn't indicate EMAIL but message may be selected in DOM
  if (isValidGoogleMailUrl(tabUrl)) {
    try {
      const messageId = await getGmailMessageId();
      if (messageId) return ItemType.EMAIL;
    } catch {
      // No message selected
    }
  }

  return null;
}

/**
 * Extracts the Gmail message ID from the currently active Gmail tab.
 *
 * This function injects a content script using the Chrome Scripting API to query for
 * `div[data-message-id]` elements in the Gmail web UI.
 *
 * @returns Promise resolving to the Gmail message ID as a string.
 *
 * @throws Error if no tab ID is available, if a message ID is not found, or on script execution errors.
 */
export async function getGmailMessageId(): Promise<string> {
  const targetTabId = getCurrentTabId();
  if (!targetTabId) throw new Error("No tab ID found");

  const results = await chrome.scripting.executeScript({
    target: { tabId: targetTabId },
    func: findActiveGmailMessageId,
  });

  let messageId = results[0]?.result;
  if (messageId) {
    if (messageId.startsWith("#")) {
      messageId = messageId.substring(1);
    }
    return messageId;
  }

  throw new Error("No message ID found");
}

/**
 * Extracts the Google Calendar meeting title from the currently active Calendar tab.
 *
 * This function injects a content script using the Chrome Scripting API to query for the
 * input element with aria-label="Title" in the Google Calendar web UI and extracts its value.
 *
 * @returns Promise<string> A promise that resolves to the calendar meeting title as a string.
 *
 * @throws Error if no tab ID is available, if a meeting title is not found, or on script execution errors.
 */
export async function getCalendarMeetingTitle(): Promise<string> {
  const targetTabId = getCurrentTabId();
  if (!targetTabId) throw new Error("No tab ID found");

  const results = await chrome.scripting.executeScript({
    target: { tabId: targetTabId },
    func: () => {
      const titleInput = document.querySelector<HTMLInputElement>('input[aria-label="Title"]');
      return titleInput?.value || undefined;
    },
  });

  const title = results[0]?.result;

  if (title) {
    return title;
  }

  throw new Error("No meeting title found");
}

/**
 * Extracts and decodes the Google Calendar event ID from the URL.
 *
 * The ID after /eventedit/ is base64 encoded and contains event information.
 * The decoded data contains an event ID and calendar ID separated by a space.
 *
 * @returns Promise resolving to an object with eventId and calendarId, or undefined if not found or invalid
 */
export async function getCalendarEventData(): Promise<CalendarEventData | undefined> {
  const tabUrl = await getCurrentTabUrl();
  if (!tabUrl) return undefined;

  // Check if we're on a Calendar event edit URL
  if (!tabUrl.includes(GOOGLE_CALENDAR_DOMAIN) || !tabUrl.includes("/eventedit/")) {
    return undefined;
  }

  try {
    // Extract the base64 ID from the URL
    const urlParts = tabUrl.split("/eventedit/");
    if (urlParts.length < 2) return undefined;

    const base64Part = urlParts[1].split(/[/?#]/)[0];
    if (!base64Part) return undefined;

    const decodedData = atob(base64Part);

    // Decode the base64 string and parse the decoded data (format: "meetingId calendarId")
    const [meetingId, calendarId] = decodedData.split(" ");
    if (!meetingId || !calendarId) return undefined;

    return { meetingId, calendarId };
  } catch {
    return undefined;
  }
}

/**
 * Gets the email address of the currently signed-in Google account
 * by extracting it from the Gmail or Google Calendar page DOM.
 *
 * This works by looking for meta tags with the account information:
 * - meta[name="og-profile-acct"]
 * - meta[property="og-profile-acct"]
 *
 * @returns Promise resolving to the current Google account email or undefined if not found
 */
export async function getCurrentGoogleAccountEmail(): Promise<string | undefined> {
  const tabId = getCurrentTabId();
  const tabUrl = await getCurrentTabUrl();
  if (!tabId || !tabUrl) return undefined;

  const isValidGoogleUrl = isValidGoogleMailUrl(tabUrl) || isValidGoogleCalendarUrl(tabUrl);

  if (!isValidGoogleUrl) {
    return undefined;
  }

  try {
    const results = await chrome.scripting.executeScript({
      target: { tabId },
      func: () => {
        // Method 1: Look for meta tag with name "og-profile-acct"
        const metaTag = document.querySelector('meta[name="og-profile-acct"]');
        if (metaTag) {
          return metaTag.getAttribute("content");
        }

        // Method 2: Look for meta tag with property "og-profile-acct"
        const altMetaTag = document.querySelector('meta[property="og-profile-acct"]');
        if (altMetaTag) {
          return altMetaTag.getAttribute("content");
        }

        return undefined;
      },
    });

    return results[0]?.result;
  } catch (error) {
    return undefined;
  }
}

/**
 * Validates if the current Google account email matches the provided grant email.
 *
 * @param googleGrantEmail - The email from the user's Google grant
 * @returns Promise resolving to true if emails don't match (mismatch), false otherwise
 */
export async function validateEmailMismatch(googleGrantEmail: string | null): Promise<boolean> {
  const userGoogleAccountEmail = await getCurrentGoogleAccountEmail();

  if (userGoogleAccountEmail && googleGrantEmail) {
    return userGoogleAccountEmail.trim().toLowerCase() !== googleGrantEmail.trim().toLowerCase();
  }

  return false;
}

/**
 * Adds a listener to detect when the URL of the Chrome tab associated with the task pane changes.
 * This is useful for tracking URL-based navigation changes within a single tab.
 *
 * @param callback - Callback invoked with the new URL whenever it changes
 * @returns A cleanup function you can call to remove the listener when no longer needed
 */
export function onUrlChange(callback: (url: string) => void): () => void {
  const tid = getCurrentTabId();

  const urlChangeListener = (tabId: number, changeInfo: { url?: string; status?: string }) => {
    if (changeInfo.url && tabId === tid) {
      callback(changeInfo.url);
    }
  };

  chrome.tabs.onUpdated.addListener(urlChangeListener);

  return () => chrome.tabs.onUpdated.removeListener(urlChangeListener);
}

/**
 * Sets up a MutationObserver in the Gmail tab to detect when the selected message changes
 * in split view mode (where the URL doesn't change).
 *
 * This injects a content script that:
 * 1. Watches for changes to div[data-message-id] (message pane loads)
 * 2. Watches for row selection changes (class `yO` on `tr` elements)
 * 3. Sends a message via chrome.runtime.sendMessage() when selection changes
 *
 * @param callback - Callback invoked when the selected Gmail message changes
 * @returns A cleanup function to remove the listener
 */
export function onGmailSelectionChange(callback: () => void): () => void {
  const tid = getCurrentTabId();
  if (!tid) return () => {};

  let messageListenerAdded = false;

  const messageListener = (message: { type: string; messageId?: string }) => {
    if (message.type === "GMAIL_SELECTION_CHANGED") {
      callback();
    }
  };

  void getCurrentTabUrl().then((tabUrl) => {
    if (getGmailViewMode(tabUrl) !== GmailViewMode.SPLIT_VIEW) return;

    void chrome.scripting.executeScript({
      target: { tabId: tid },
      func: setupGmailSelectionObserver,
    });

    messageListenerAdded = true;
    chrome.runtime.onMessage.addListener(messageListener);
  });

  return () => {
    if (messageListenerAdded) {
      chrome.runtime.onMessage.removeListener(messageListener);
    }
  };
}
